/**
 * Score-based product matching utilities
 * Used to find the best matching product for an itinerary item title/description
 */

export interface ProductForMatching {
  id: string;
  product_name: string;
  hero_image_url: string | null;
}

// Common modifiers that should be ignored during matching
const STOPWORDS = new Set([
  'morning', 'afternoon', 'evening', 'night',
  'full', 'half', 'day', 'days',
  'private', 'shared', 'guided',
  'the', 'and', 'with', 'for', 'from', 'to'
]);

/**
 * Filter out stopwords from a word array
 */
const filterSignificantWords = (words: string[]): string[] => {
  return words.filter(w => w.length > 2 && !STOPWORDS.has(w));
};

/**
 * Score a product match - higher score = better match
 * Exact matches get 1000 points, partial matches are scored by coverage and match ratio
 * Uses 80% threshold with stopwords filtering for flexible matching
 */
export const scoreProductMatch = (
  normalizedTitle: string,
  normalizedProductName: string
): number => {
  // Exact match gets highest priority
  if (normalizedProductName === normalizedTitle) return 1000;
  
  // Get all words for matching
  const allTitleWords = normalizedTitle.split(/\s+/).filter(w => w.length > 2);
  const allProductWords = normalizedProductName.split(/\s+/).filter(w => w.length > 2);
  
  // Filter to significant words only (ignore stopwords)
  const significantTitleWords = filterSignificantWords(allTitleWords);
  const significantProductWords = filterSignificantWords(allProductWords);
  
  // No significant words = no match
  if (significantTitleWords.length === 0 || significantProductWords.length === 0) return 0;
  
  // Count how many significant title words appear in the product name
  const matchedWords = significantTitleWords.filter(tw => significantProductWords.includes(tw)).length;
  
  // No words match = no match at all
  if (matchedWords === 0) return 0;
  
  // Calculate match ratio - require at least 80% of significant words to match
  const matchRatio = matchedWords / significantTitleWords.length;
  if (matchRatio < 0.8) return 0;
  
  // Score based on:
  // 1. Match ratio (how many title words were found)
  // 2. Overlap density (how specific is the match - fewer extra words = better)
  const overlapDensity = matchedWords / significantProductWords.length;
  
  // Higher match ratio + higher density = better score (max ~100)
  return Math.round(matchRatio * 50 + overlapDensity * 50);
};

/**
 * Find the best matching product for an itinerary item
 * Returns the product with the highest specificity score, or null if no match
 */
export const findBestProductMatch = (
  normalizedTitle: string,
  normalizedDesc: string,
  products: ProductForMatching[] | null
): ProductForMatching | null => {
  if (!products || products.length === 0) return null;
  
  let bestMatch: ProductForMatching | null = null;
  let bestScore = 0;
  
  for (const product of products) {
    const normalizedProductName = (product.product_name || '').trim().toLowerCase();
    
    // Try matching against title
    const titleScore = scoreProductMatch(normalizedTitle, normalizedProductName);
    if (titleScore > bestScore) {
      bestScore = titleScore;
      bestMatch = product;
    }
    
    // Also try matching against description
    const descScore = scoreProductMatch(normalizedDesc, normalizedProductName);
    if (descScore > bestScore) {
      bestScore = descScore;
      bestMatch = product;
    }
  }
  
  return bestScore > 0 ? bestMatch : null;
};
